/**
 * RoboPixel readymade PDP v2 controller.
 *
 * State is deliberately split into `selectedId` (the visible preview) and
 * `confirmed` (permission to purchase). A valid ?variant= is authoritative;
 * remembered/default previews require an explicit confirmation.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'robopixel:readymade:model-suffix';

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function imageUrlAtWidth(url, width) {
    if (!url) return '';
    try {
      var parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch (error) {
      var separator = String(url).indexOf('?') === -1 ? '?' : '&';
      return String(url).replace(/([?&])width=\d+/, '$1width=' + width) +
        (String(url).match(/([?&])width=\d+/) ? '' : separator + 'width=' + width);
    }
  }

  function responsiveImageSrcset(url) {
    return [400, 600, 800, 1000, 1200]
      .map(function (width) { return imageUrlAtWidth(url, width) + ' ' + width + 'w'; })
      .join(', ');
  }

  function parseJSON(root, selector) {
    var node = root.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  if (customElements.get('readymade-product-v2')) return;

  customElements.define(
    'readymade-product-v2',
    class ReadymadeProductV2 extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';

        this.startedAt = window.performance && performance.now ? performance.now() : Date.now();
        this.variants = parseJSON(this, '[data-rpv2-variants]') || {};
        this.galleries = parseJSON(this, '[data-rpv2-gallery]') || {};
        this.i18n = parseJSON(this, '[data-rpv2-i18n]') || {};
        this.offerConfig = parseJSON(this, '[data-rp-offers-json]') || {};
        this.forms = Array.from(this.querySelectorAll('[data-rpv2-form]'));
        this.atcButtons = Array.from(this.querySelectorAll('[data-rpv2-atc]'));
        this.radios = Array.from(this.querySelectorAll('[data-rpv2-model]'));
        this.variantInputs = Array.from(this.querySelectorAll('[data-rpv2-variant-input]'));
        this.modelValues = Array.from(this.querySelectorAll('[data-rpv2-model-value]'));
        this.price = this.querySelector('[data-rpv2-price]');
        this.offerRadios = Array.from(this.querySelectorAll('[data-rp-offer]'));
        var checkedOffer = this.offerRadios.find(function (radio) { return radio.checked && !radio.disabled; });
        this.offer = checkedOffer ? checkedOffer.value : 'case';
        this.offerRadios.forEach(function (radio) { radio.checked = radio.value === this.offer; }, this);
        this.caseOfferPrices = Array.from(this.querySelectorAll('[data-rp-offer-case-price]'));
        this.ecosystemOption = this.querySelector('[data-rpv2-ecosystem-option]');
        this.ecosystemStatus = this.querySelector('[data-rpv2-ecosystem-status]');
        this.modelStatus = this.querySelector('[data-rpv2-model-status]');
        this.pickerValue = this.querySelector('[data-rpv2-picker-value]');
        this.confirmLabel = this.querySelector('[data-rpv2-confirm-label]');
        this.galleryMain = this.querySelector('[data-rpv2-gallery-main]');
        this.galleryThumbs = this.querySelector('[data-rpv2-thumbs]');
        this.galleryStatus = this.querySelector('[data-rpv2-gallery-status]');
        this.error = this.querySelector('[data-rpv2-error]');
        this.picker = this.querySelector('[data-rpv2-picker]');
        this.sticky = this.querySelector('[data-rpv2-sticky]');
        this.lastPickerFocus = null;

        this.selectedId = String(this.dataset.defaultVariant || '');
        this.variantSource = this.dataset.variantSource || 'fallback';
        this.confirmed = this.dataset.urlAuthoritative === 'true';

        this.bindPicker();
        this.bindModels();
        this.bindOffers();
        this.bindForms();
        this.bindSticky();
        this.bindRelated();
        this.bindProof();
        this.bindUpload();

        if (!this.confirmed) {
          var remembered = this.readRememberedSuffix();
          var rememberedVariant = this.findAvailableBySuffix(remembered);
          if (rememberedVariant) {
            this.selectedId = String(rememberedVariant.id);
            this.variantSource = 'remembered';
          }
        }

        this.select(this.selectedId, {
          source: this.variantSource,
          confirmed: this.confirmed,
          updateUrl: false,
          announce: false
        });

        this.publish('robopixel:readymade_view', this.eventPayload());
      }

      text(key, replacements) {
        var output = this.i18n[key] || '';
        Object.keys(replacements || {}).forEach(function (token) {
          output = output.split(token).join(replacements[token]);
        });
        return output;
      }

      elapsed() {
        var now = window.performance && performance.now ? performance.now() : Date.now();
        return Math.max(0, Math.round(now - this.startedAt));
      }

      eventPayload(extra) {
        var variant = this.variants[this.selectedId] || {};
        return Object.assign(
          {
            product_id: this.dataset.productId || '',
            product_handle: this.dataset.productHandle || '',
            design: this.dataset.designName || '',
            tribe: this.dataset.tribe || '',
            locale: this.dataset.locale || '',
            variant_id: this.selectedId || '',
            model: variant.title || '',
            model_suffix: variant.suffix || '',
            variant_source: this.variantSource || '',
            offer: this.offer || '',
            confirmed: !!this.confirmed,
            elapsed_ms: this.elapsed()
          },
          extra || {}
        );
      }

      publish(name, payload) {
        try {
          if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
            window.Shopify.analytics.publish(name, payload || {});
          }
        } catch (error) {
          // Analytics must never block the purchase path.
        }
      }

      readRememberedSuffix() {
        try {
          return window.localStorage.getItem(STORAGE_KEY) || '';
        } catch (error) {
          return '';
        }
      }

      rememberSuffix(suffix) {
        if (!suffix) return;
        try {
          window.localStorage.setItem(STORAGE_KEY, suffix);
        } catch (error) {
          // Storage can be unavailable in privacy modes; confirmation still works.
        }
      }

      findAvailableBySuffix(suffix) {
        if (!suffix) return null;
        var ids = Object.keys(this.variants);
        for (var i = 0; i < ids.length; i += 1) {
          var candidate = this.variants[ids[i]];
          if (candidate && candidate.available && candidate.suffix === suffix) {
            return Object.assign({ id: ids[i] }, candidate);
          }
        }
        return null;
      }

      select(variantId, options) {
        var opts = options || {};
        var id = String(variantId || '');
        var variant = this.variants[id];
        if (!variant) return;

        this.selectedId = id;
        this.variantSource = opts.source || this.variantSource || 'manual';
        if (typeof opts.confirmed === 'boolean') this.confirmed = opts.confirmed;

        this.variantInputs.forEach(function (input) {
          input.value = id;
        });
        this.radios.forEach(function (radio) {
          radio.checked = radio.value === id;
        });
        this.modelValues.forEach(function (node) {
          node.textContent = variant.title;
        });
        if (this.pickerValue) this.pickerValue.textContent = variant.title;
        if (this.confirmLabel) {
          this.confirmLabel.textContent = this.text('confirm_model', { __MODEL__: variant.title });
        }
        this.caseOfferPrices.forEach(function (node) { node.innerHTML = variant.price_html; });

        this.showBrand(variant.brand, false);
        this.updateOfferAvailability();
        this.updateOfferUI();
        this.updateButtons();
        this.updateGallery(id, variant.title, opts.announce !== false);
        this.updateRelated(variant.suffix, variant.title);

        if (opts.updateUrl) this.updateUrl(id);
      }

      updateButtons() {
        this.updateUpload();
        var variant = this.variants[this.selectedId];
        if (!variant) return;
        var offerChosen = !!this.offer || this.offerRadios.length === 0;
        var purchasable = this.confirmed && variant.available && offerChosen;
        var self = this;

        this.atcButtons.forEach(function (button) {
          button.type = purchasable ? 'submit' : 'button';
          button.disabled = !variant.available || !!self.addingToCart;
          button.dataset.confirmed = self.confirmed ? 'true' : 'false';
        });

        var longLabel = this.querySelector('[data-rpv2-atc-label]');
        var shortLabel = this.querySelector('[data-rpv2-atc-label-short]');
        if (!variant.available) {
          if (longLabel) longLabel.textContent = this.text('sold_out');
          if (shortLabel) shortLabel.textContent = this.text('sold_out');
        } else if (this.confirmed && !offerChosen) {
          [longLabel, shortLabel].forEach(function (label) {
            if (label) label.innerHTML = '<span class="rp-atc-action">' + escapeHtml(self.text('offer_choose')) + '</span><span class="rp-atc-summary">' + escapeHtml(self.text('offer_choose_hint')) + '</span>';
          });
        } else if (this.confirmed) {
          var offer = this.selectedOfferSummary();
          var summary = variant.title + ' · ' + offer.name + ' · ' + offer.price;
          [longLabel, shortLabel].forEach(function (label) {
            if (label) label.innerHTML = '<span class="rp-atc-action">' + escapeHtml(self.text(self.isSecondCase() ? 'offer_pair_complete' : self.offer === 'pair' ? 'offer_pair_start' : 'offer_add')) + '</span><span class="rp-atc-summary">' + escapeHtml(summary) + '</span>';
          });
        } else {
          var pendingOffer = this.selectedOfferSummary();
          var pendingSummary = pendingOffer.name + ' · ' + pendingOffer.price;
          if (longLabel) longLabel.innerHTML = '<span class="rp-atc-action">' + escapeHtml(this.text('choose_model')) + '</span><span class="rp-atc-summary">' + escapeHtml(pendingSummary) + '</span>';
          if (shortLabel) shortLabel.innerHTML = '<span class="rp-atc-action">' + escapeHtml(this.text('choose_short')) + '</span><span class="rp-atc-summary">' + escapeHtml(pendingSummary) + '</span>';
        }

        if (this.modelStatus) {
          this.modelStatus.textContent = this.confirmed ? this.text('confirmed') : this.text('preview');
        }
      }

      updateUpload() {
        var link = this.querySelector('[data-rpv2-upload]');
        if (!link) return;
        var url = new URL(link.dataset.uploadUrl, window.location.origin);
        var variant = this.variants[this.selectedId];
        // Only an explicitly confirmed phone may bypass the customizer picker.
        // Otherwise reuse its upload-first preview and existing purchase gate.
        if (this.confirmed && variant) {
          url.searchParams.set('model', variant.title);
        } else {
          url.searchParams.set('start', 'upload');
        }
        link.href = url.pathname + url.search;
      }

      bindUpload() {
        var self = this;
        var link = this.querySelector('[data-rpv2-upload]');
        if (!link) return;
        link.addEventListener('click', function () {
          self.publish('robopixel:original_upload_clicked', self.eventPayload({
            placement: 'below_design_name', method: 'upload'
          }));
        });
      }

      bindOffers() {
        var self = this;
        window.addEventListener('robopixel:pair-context', function () {
          if (!self.isConnected) return;
          self.updateOfferUI();
          self.updateButtons();
        });
        this.offerRadios.forEach(function (radio) {
          radio.addEventListener('change', function () {
            if (!radio.checked || radio.disabled) return;
            self.offer = radio.value;
            self.clearOfferPrompt();
            self.updateOfferUI();
            self.updateButtons();
            self.publish('robopixel:offer_selected', self.eventPayload({ offer: self.offer }));
          });
        });
      }

      clearOfferPrompt() {
        var selector = this.querySelector('[data-rp-offers]');
        if (!selector) return;
        selector.classList.remove('is-attention');
        selector.removeAttribute('aria-invalid');
        var message = selector.querySelector('[data-offer-selection-message]');
        if (message) message.hidden = true;
      }

      ensureOfferSelected(trigger) {
        if (this.offer || this.offerRadios.length === 0) {
          if (!this.offer) this.offer = 'case';
          return true;
        }
        var selector = this.querySelector('[data-rp-offers]');
        if (!selector) {
          this.offer = 'case';
          return true;
        }
        selector.classList.add('is-attention');
        selector.setAttribute('aria-invalid', 'true');
        var message = selector.querySelector('[data-offer-selection-message]');
        if (message) message.hidden = false;
        var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        selector.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        var firstAvailable = this.offerRadios.find(function (radio) { return !radio.disabled; });
        if (firstAvailable) {
          window.requestAnimationFrame(function () {
            try { firstAvailable.focus({ preventScroll: true }); }
            catch (error) { firstAvailable.focus(); }
          });
        }
        this.publish('robopixel:offer_required', this.eventPayload({ trigger: trigger && trigger.className || '' }));
        return false;
      }

      ecosystemForVariant() {
        var variant = this.variants[this.selectedId] || {};
        var ecosystem = this.offerConfig.ecosystem || {};
        var protector = ecosystem.protectors && ecosystem.protectors[variant.suffix];
        // The pack is one bundle variant. Without a bundle entry for this phone
        // model the pack is simply unavailable - it must never fall back to
        // adding the three items separately at their full separate price.
        var bundle = ecosystem.bundle && ecosystem.bundle[this.selectedId];
        return {
          available: !!(ecosystem.enabled && variant.available && protector && protector.available && ecosystem.card && ecosystem.card.available && bundle && bundle.available),
          protector: protector,
          card: ecosystem.card,
          bundle: bundle
        };
      }

      updateOfferAvailability() {
        var state = this.ecosystemForVariant();
        var variant = this.variants[this.selectedId] || {};
        var available = { case: !!variant.available, pair: !!variant.available, protection: this.protectionForVariant().available, ecosystem: state.available };
        // Owner-confirmed Ultimate Pack status; preserve unavailable model handling.
        var lowStock = state.available;
        var self = this;
        this.offerRadios.forEach(function (radio) {
          var active = available[radio.value];
          radio.disabled = !active;
          radio.closest('.rp-offer').classList.toggle('is-unavailable', !active);
          // A pack that does not exist for this phone model is not offered at
          // all: a Galaxy shopper sees the case as the only choice.
          if (radio.value === 'ecosystem' || radio.value === 'protection') radio.closest('.rp-offer').hidden = !active;
          var status = radio.closest('.rp-offer').querySelector('[data-offer-status]');
          if (status) {
            status.dataset.available = String(active);
            status.dataset.lowStock = String(active && radio.value === 'ecosystem' && lowStock);
            status.textContent = active ? self.text(radio.value === 'ecosystem' && lowStock ? 'offer_low_stock' : 'offer_available') : radio.value === 'ecosystem' && variant.brand !== 'apple' ? self.text('ecosystem_unavailable') : self.text('offer_unavailable');
          }
        });
        var alternative = this.querySelector('[data-pair-alternative]');
        if (alternative) alternative.hidden = !(available.protection || available.ecosystem);
        if (this.offer && !available[this.offer]) {
          this.offer = available.case ? 'case' : '';
          this.offerRadios.forEach(function (radio) { radio.checked = radio.value === self.offer; });
        }
      }

      // The Protection Pack is one bundle variant: the case plus one protector
      // for the same phone. No bundle for this model means no pack - it never
      // falls back to adding the two items separately.
      protectionForVariant() {
        var variant = this.variants[this.selectedId] || {};
        var protection = this.offerConfig.protection || {};
        var pack = protection.packs && protection.packs[this.selectedId];
        return { available: !!(protection.enabled && variant.available && pack && pack.available), pack: pack };
      }

      // A pack always shows its own bundle price, pair in progress or not.
      protectionPriceHtml() {
        var pack = this.protectionForVariant().pack;
        return pack ? pack.price_html : '';
      }

      isSecondCase() {
        return this.offer === 'case' && !!(this.offerConfig.pair && this.offerConfig.pair.enabled && window.RoboPixelPairJourney && window.RoboPixelPairJourney.isSecond());
      }

      selectedOfferSummary() {
        var variant = this.variants[this.selectedId] || {};
        if (!this.offer) return { name: this.text('offer_choose'), price: '' };
        var names = { case: 'offer_case_name', pair: 'offer_pair_name', protection: 'offer_protection_name', ecosystem: 'offer_pack_name' };
        // The CTA names the one case added now, not the unfinished pair total.
        var price = this.offer === 'case' || this.offer === 'pair' ? variant.price_html
          : this.offer === 'protection' ? this.protectionPriceHtml() : (this.offerConfig[this.offer] || {}).price_html;
        if (this.isSecondCase()) price = (window.RoboPixelPairJourney.displayPrice?.(this.offerConfig.second_price_html) || this.offerConfig.second_price_html);
        var plain = document.createElement('span');
        plain.innerHTML = price || '';
        return { name: this.isSecondCase() ? (window.RoboPixelPairJourney.displayName?.(this.text('offer_pair_name')) || this.text('offer_pair_name')) : this.text(names[this.offer]), price: plain.textContent };
      }

      updateOfferUI() {
        var current = this.variants[this.selectedId] || {};
        if (window.RoboPixelPairJourney) {
          var second = window.RoboPixelPairJourney.decorate(this, this.offerConfig.pair && this.offerConfig.pair.enabled, current.price_html || '', this.offerConfig.second_price_html);
          if (second && this.offer === 'pair') {
            this.offer = 'case';
            this.offerRadios.forEach(function (radio) { radio.checked = radio.value === 'case'; });
          }
        }
        var startingPair = this.offer === 'pair';
        this.querySelectorAll('[data-pair-guidance]').forEach(function (node) { node.hidden = !startingPair; });
        var variant = this.variants[this.selectedId] || {};
        var priceHtml = variant.price_html || '';
        if (this.isSecondCase()) priceHtml = (window.RoboPixelPairJourney.displayPrice?.(this.offerConfig.second_price_html) || this.offerConfig.second_price_html);
        if (this.offer === 'pair' && this.offerConfig.pair) priceHtml = this.offerConfig.pair.price_html;
        if (this.offer === 'ecosystem' && this.offerConfig.ecosystem) priceHtml = this.offerConfig.ecosystem.price_html;
        var protectionHtml = this.protectionPriceHtml();
        if (protectionHtml) this.querySelectorAll('[data-rp-protection-price]').forEach(function (node) { node.innerHTML = protectionHtml; });
        if (this.offer === 'protection' && protectionHtml) priceHtml = protectionHtml;
        if (this.price) this.price.innerHTML = priceHtml;
        var showcase = this.querySelector('ecosystem-showcase');
        if (showcase) {
          showcase.hidden = this.offer !== 'ecosystem';
          showcase.dataset.model = variant.title || '';
          showcase.querySelectorAll('[data-ecosystem-model]').forEach(function (node) { node.textContent = variant.title || ''; });
          if (showcase.hidden) {
            showcase.dispatchEvent(new Event('ecosystem:collapse'));
            showcase.querySelectorAll('details[open]').forEach(function (details) { details.open = false; });
            var dialog = showcase.querySelector('dialog[open]');
            if (dialog) dialog.close();
          }
        }
      }

      updateUrl(variantId) {
        if (!window.history || !window.history.replaceState) return;
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url.toString());
      }

      updateGallery(variantId, modelTitle, announce) {
        if (!this.galleryMain) return;
        var images = this.galleries[variantId] || [];
        var alt = this.text('alt', { __MODEL__: modelTitle });
        var self = this;
        // Keep the Liquid image (and its in-flight preload) on first hydration.
        // URL-selected and remembered phones still rebuild when they differ.
        var keepRenderedGallery = this.galleryMain.dataset.renderedVariant === String(variantId) &&
          this.galleryMain.querySelector('[data-rpv2-slide] img');

        if (!images.length) {
          this.galleryMain.innerHTML = '<div class="rpv2-gallery__placeholder">' + escapeHtml(this.text('placeholder')) + '</div>';
          if (this.galleryThumbs) {
            this.galleryThumbs.hidden = true;
            this.galleryThumbs.innerHTML = '';
          }
        } else if (keepRenderedGallery) {
          if (this.galleryThumbs && !this.galleryThumbs.childElementCount) this.renderThumbs(images, alt);
        } else {
          this.galleryMain.innerHTML = images
            .map(function (image, index) {
              var imageUrl = escapeHtml(imageUrlAtWidth(image.url, 1200));
              var imageSrcset = escapeHtml(responsiveImageSrcset(image.url));
              var imageAttributes = index
                ? 'src="data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=" data-rp-src="' + imageUrl +
                  '" data-rp-srcset="' + imageSrcset + '" class="rp-deferred-gallery-image" loading="lazy" decoding="async"'
                : 'src="' + escapeHtml(imageUrlAtWidth(image.url, 800)) + '" srcset="' + imageSrcset +
                  '" fetchpriority="high"';
              return (
                '<figure class="rpv2-gallery__slide" data-rpv2-slide="' + index + '"' + (index ? ' hidden' : '') + '>' +
                '<img ' + imageAttributes + ' sizes="(max-width: 749px) calc(100vw - 3.2rem), (max-width: 1199px) 50vw, 62rem" alt="' + escapeHtml(alt) + '" width="' +
                escapeHtml(image.width || 1254) + '" height="' + escapeHtml(image.height || 1254) + '"' +
                '></figure>'
              );
            })
            .join('');
          this.renderThumbs(images, alt);
        }
        this.galleryMain.dataset.renderedVariant = String(variantId);
        this.dispatchEvent(new CustomEvent('robopixel:readymade-gallery'));

        if (announce && this.galleryStatus) {
          this.galleryStatus.textContent = this.text('gallery_status', { __MODEL__: modelTitle });
        }

        // Ensure the first rendered image is decoded without delaying state sync.
        var first = self.galleryMain.querySelector('img');
        if (first && typeof first.decode === 'function') first.decode().catch(function () {});
      }

      renderThumbs(images, alt) {
        if (!this.galleryThumbs) return;
        if (images.length < 2) {
          this.galleryThumbs.hidden = true;
          this.galleryThumbs.innerHTML = '';
          return;
        }

        var self = this;
        var total = String(images.length);
        this.galleryThumbs.hidden = false;
        this.galleryThumbs.innerHTML = images
          .map(function (image, index) {
            var label = self.text('thumb', {
              __NUMBER__: String(index + 1),
              __TOTAL__: total
            });
            return (
              '<button type="button" class="rpv2-gallery__thumb" data-rpv2-thumb="' + index + '"' +
              ' aria-label="' + escapeHtml(label) + '" aria-current="' + (index === 0 ? 'true' : 'false') + '">' +
              '<img src="' + escapeHtml(imageUrlAtWidth(image.url, 160)) + '" alt="" width="80" height="80" loading="lazy" decoding="async"></button>'
            );
          })
          .join('');

        this.galleryThumbs.querySelectorAll('[data-rpv2-thumb]').forEach(function (button) {
          button.addEventListener('click', function () {
            self.showSlide(parseInt(button.dataset.rpv2Thumb, 10));
          });
        });
      }

      showSlide(index) {
        if (!this.galleryMain) return;
        this.galleryMain.querySelectorAll('[data-rpv2-slide]').forEach(function (slide) {
          var isSelected = parseInt(slide.dataset.rpv2Slide, 10) === index;
          if (isSelected) {
            var image = slide.querySelector('img[data-rp-src]');
            if (image) {
              if (image.dataset.rpSrcset) image.srcset = image.dataset.rpSrcset;
              image.src = image.dataset.rpSrc;
              image.removeAttribute('data-rp-src');
              image.removeAttribute('data-rp-srcset');
              image.classList.remove('rp-deferred-gallery-image');
            }
          }
          slide.hidden = !isSelected;
        });
        if (this.galleryThumbs) {
          this.galleryThumbs.querySelectorAll('[data-rpv2-thumb]').forEach(function (button) {
            button.setAttribute('aria-current', parseInt(button.dataset.rpv2Thumb, 10) === index ? 'true' : 'false');
          });
        }
      }

      bindModels() {
        var self = this;
        this.radios.forEach(function (radio) {
          radio.addEventListener('change', function () {
            if (!radio.checked) return;
            self.select(radio.value, {
              source: 'manual',
              confirmed: false,
              updateUrl: false,
              announce: true
            });
          });
        });

        var confirm = this.querySelector('[data-rpv2-confirm]');
        if (confirm) {
          confirm.addEventListener('click', function () {
            var variant = self.variants[self.selectedId];
            if (!variant || !variant.available) return;
            self.confirmed = true;
            self.variantSource = self.variantSource === 'url' ? 'url' : 'manual';
            self.rememberSuffix(variant.suffix);
            self.updateButtons();
            self.updateRelated(variant.suffix, variant.title);
            self.updateUrl(self.selectedId);
            self.publish('robopixel:model_confirmed', self.eventPayload());
            self.closePicker();
          });
        }
      }

      bindPicker() {
        var self = this;
        this.querySelectorAll('[data-rpv2-picker-open]').forEach(function (button) {
          button.addEventListener('click', function () {
            self.openPicker(button);
          });
        });
        this.querySelectorAll('[data-rpv2-picker-close]').forEach(function (button) {
          button.addEventListener('click', function () {
            self.closePicker();
          });
        });
        this.querySelectorAll('[data-rpv2-brand]').forEach(function (button) {
          button.addEventListener('click', function () {
            self.showBrand(button.dataset.rpv2Brand, true);
          });
        });
        this.onPickerKeydown = function (event) {
          if (!self.picker || self.picker.hidden) return;
          if (event.key === 'Escape') {
            event.preventDefault();
            self.closePicker();
            return;
          }
          if (event.key !== 'Tab') return;
          var focusable = Array.from(
            self.picker.querySelectorAll('button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])')
          ).filter(function (node) {
            return !node.hidden && node.offsetParent !== null;
          });
          if (!focusable.length) return;
          var first = focusable[0];
          var last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        };
      }

      showBrand(brand, publishEvent) {
        var target = brand === 'samsung' ? 'samsung' : 'apple';
        this.querySelectorAll('[data-rpv2-brand]').forEach(function (button) {
          var active = button.dataset.rpv2Brand === target;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-selected', active ? 'true' : 'false');
          button.tabIndex = active ? 0 : -1;
        });
        this.querySelectorAll('[data-rpv2-brand-panel]').forEach(function (panel) {
          panel.hidden = panel.dataset.rpv2BrandPanel !== target;
        });
        var selected = this.variants[this.selectedId];
        var canConfirm = !!(selected && selected.available && selected.brand === target);
        var confirm = this.querySelector('[data-rpv2-confirm]');
        if (confirm) confirm.disabled = !canConfirm;
        if (this.confirmLabel) {
          this.confirmLabel.textContent = canConfirm
            ? this.text('confirm_model', { __MODEL__: selected.title })
            : this.text('choose_model');
        }
        if (publishEvent) {
          this.publish('robopixel:brand_selected', this.eventPayload({ brand: target }));
        }
      }

      openPicker(trigger) {
        if (!this.picker) return;
        this.lastPickerFocus = trigger || document.activeElement;
        this.picker.hidden = false;
        this.picker.dataset.open = 'true';
        document.body.classList.add('rpv2-dialog-open');
        document.addEventListener('keydown', this.onPickerKeydown);
        var checked = this.picker.querySelector('[data-rpv2-model]:checked');
        var focusTarget = checked && !checked.disabled ? checked : this.picker.querySelector('[data-rpv2-picker-close]');
        if (focusTarget) focusTarget.focus({ preventScroll: true });
        if (checked) checked.scrollIntoView({ block: 'nearest' });
        this.publish('robopixel:model_selector_opened', this.eventPayload());
      }

      closePicker() {
        if (!this.picker || this.picker.hidden) return;
        this.picker.dataset.open = 'false';
        this.picker.hidden = true;
        document.body.classList.remove('rpv2-dialog-open');
        document.removeEventListener('keydown', this.onPickerKeydown);
        if (this.lastPickerFocus && typeof this.lastPickerFocus.focus === 'function') this.lastPickerFocus.focus();
      }

      bindForms() {
        var self = this;
        this.atcButtons.forEach(function (button) {
          button.addEventListener('click', function (event) {
            if (!self.confirmed) {
              event.preventDefault();
              self.openPicker(button);
              return;
            }
            if (!self.ensureOfferSelected(button)) event.preventDefault();
          });
        });
        this.forms.forEach(function (form) {
          form.addEventListener('submit', function (event) {
            event.preventDefault();
            if (!self.confirmed) {
              self.openPicker(form.querySelector('[data-rpv2-atc]'));
              return;
            }
            if (!self.ensureOfferSelected(form.querySelector('[data-rpv2-atc]'))) return;
            self.addToCart(form);
          });
        });
      }

      async addToCart(form) {
        if (this.addingToCart) return;
        var variant = this.variants[this.selectedId];
        if (!this.confirmed || !variant || !variant.available || !this.ensureOfferSelected(form && form.querySelector('[data-rpv2-atc]'))) return;
        var ecosystemState = this.ecosystemForVariant();
        if (this.offer === 'ecosystem' && !ecosystemState.available) return;
        var protectionState = this.protectionForVariant();
        if (this.offer === 'protection' && !protectionState.available) return;
        var selectedOffer = this.offer;
        var secondCase = this.isSecondCase();
        var isPack = selectedOffer === 'ecosystem' || selectedOffer === 'protection';
        this.addingToCart = true;
        var self = this;
        if (this.error) this.error.hidden = true;

        this.atcButtons.forEach(function (button) {
          button.disabled = true;
          var label = button.querySelector('span');
          if (label) label.textContent = self.text('adding');
        });

        try {
          if (!isPack && this.offerConfig.pair && this.offerConfig.pair.enabled && window.RoboPixelPairJourney) {
            try { await window.RoboPixelPairJourney.validate(secondCase); }
            catch (error) { error.offerChanged = true; throw error; }
          }
          var response;
          if (selectedOffer === 'case') {
            var body = new FormData(form);
            if (secondCase) {
              body.append('properties[_RoboPixel Offer]', 'Case Pair');
              body.append('properties[_Case_Pair_First]', window.RoboPixelPairJourney.firstKey());
            }
            body.append('sections', 'cart-drawer,cart-icon-bubble');
            body.append('sections_url', window.location.pathname);
            if (secondCase) {
              var properties = {};
              body.forEach(function (value, name) {
                var match = name.match(/^properties\[(.+)\]$/);
                if (match) properties[match[1]] = value;
              });
              try {
                var checked = await window.RoboPixelPairJourney.addChecked({
                  items: [{ id: Number(this.selectedId), quantity: 1, properties: properties }],
                  sections: 'cart-drawer,cart-icon-bubble', sections_url: window.location.pathname
                }, true, Number(this.offerConfig.second_price_cents));
                response = { ok: true, json: async function () { return checked; } };
              } catch (error) { error.offerChanged = true; throw error; }
            } else {
              response = await fetch(form.action, {
                method: 'POST', headers: { Accept: 'application/json' }, body: body
              });
            }
          } else if (selectedOffer === 'protection') {
            // One bundle variant, not a case line plus a protector line. Shopify
            // prices the bundle itself and nests the two items inside the line.
            if (!protectionState.pack || !protectionState.pack.id) throw new Error('protection-bundle-missing');
            response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                items: [{ id: Number(protectionState.pack.id), quantity: 1 }],
                sections: 'cart-drawer,cart-icon-bubble',
                sections_url: window.location.pathname
              })
            });
          } else {
            var groupId = 'rp-' + Date.now() + '-' + Math.random().toString(16).slice(2);
            // A pair-START line is one case at its full price, and the second
            // case may never arrive. Flagging it "Case Pair" made single-case
            // orders claim a pair that does not exist; the completing case
            // (the FormData branch above) is the one that carries "Case Pair".
            var offerName = selectedOffer === 'ecosystem' ? 'Ecosystem' : 'Case Pair (started)';
            var items;
            if (selectedOffer === 'ecosystem') {
              // One bundle variant, not three lines. Shopify prices the bundle
              // itself, so the cart shows a single line at the pack price with
              // the case, protectors and card nested inside it.
              if (!ecosystemState.bundle || !ecosystemState.bundle.id) throw new Error('ecosystem-bundle-missing');
              items = [{ id: Number(ecosystemState.bundle.id), quantity: 1 }];
            } else {
              items = [{
                id: Number(this.selectedId),
                quantity: 1,
                properties: { '_RoboPixel Offer': offerName, '_RoboPixel Offer Group': groupId }
              }];
            }
            response = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                items: items,
                sections: 'cart-drawer,cart-icon-bubble',
                sections_url: window.location.pathname
              })
            });
          }
          if (!response.ok) throw new Error('cart-add-failed');
          var data = await response.json();
          // A pack added beside a loose case switches the Case Pair on, and the
          // sections bundled into the add response are rendered BEFORE Shopify
          // recalculates discounts. Read them again so the drawer opens showing
          // what is actually charged.
          if (isPack) {
            try {
              var fresh = await fetch(window.location.pathname + '?sections=cart-drawer,cart-icon-bubble', { credentials: 'same-origin', cache: 'no-store' });
              if (fresh.ok) data.sections = await fresh.json();
            } catch (_) {}
          }
          self.rememberSuffix(variant.suffix);

          if (window.RoboPixelLoadCart) await window.RoboPixelLoadCart().catch(function () {});
          var drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
            drawer.renderContents(data);
            if (selectedOffer === 'pair' && window.RoboPixelPairJourney) window.RoboPixelPairJourney.start(data);
          } else {
            if (selectedOffer === 'pair' && window.RoboPixelPairJourney) window.RoboPixelPairJourney.start(data);
            window.location.href = form.action.replace(/\/add(\.js)?$/, '') + (selectedOffer === 'pair' ? '?choose_second=1' : '');
            return;
          }
        } catch (error) {
          if (this.error) {
            this.error.textContent = error.offerChanged && error.message ? error.message : this.text('error');
            this.error.hidden = false;
          }
        } finally {
          this.addingToCart = false;
          this.updateButtons();
        }
      }

      bindSticky() {
        if (!this.sticky || !('IntersectionObserver' in window)) return;
        var mainButton = this.querySelector('.rpv2-buybox [data-rpv2-atc]');
        if (!mainButton) return;
        var sticky = this.sticky;
        new IntersectionObserver(function (entries) {
          sticky.dataset.visible = entries[0].isIntersecting ? 'false' : 'true';
        }).observe(mainButton);
      }

      bindRelated() {
        var self = this;
        this.querySelectorAll('[data-rpv2-related-link]').forEach(function (link) {
          link.addEventListener('click', function () {
            var card = link.closest('[data-rpv2-related-card]');
            self.publish(
              'robopixel:related_product_clicked',
              self.eventPayload({ related_design: card ? card.dataset.designName || '' : '' })
            );
          });
        });
      }

      updateRelated(suffix, modelTitle) {
        var self = this;
        this.querySelectorAll('[data-rpv2-related-card]').forEach(function (card) {
          if (!card._rpv2Variants) {
            card._rpv2Variants = parseJSON(card, '[data-rpv2-related-variants]') || {};
          }
          var match = card._rpv2Variants[suffix];
          if (!match || !match.available) {
            card.hidden = true;
            return;
          }
          card.hidden = false;
          var link = card.querySelector('[data-rpv2-related-link]');
          if (link) {
            link.href = self.confirmed
              ? card.dataset.baseUrl + '?variant=' + encodeURIComponent(match.id)
              : card.dataset.baseUrl;
          }
          var price = card.querySelector('[data-rpv2-related-price]');
          if (price) price.innerHTML = match.price;
          var image = card.querySelector('[data-rpv2-related-image]');
          var placeholder = card.querySelector('[data-rpv2-related-placeholder]');
          if (image && match.image) {
            image.src = match.image;
            image.alt = self
              .text('related_alt', {
                __DESIGN__: card.dataset.designName || '',
                __MODEL__: modelTitle
              });
            image.hidden = false;
            if (placeholder) placeholder.hidden = true;
          } else {
            if (image) image.hidden = true;
            if (placeholder) placeholder.hidden = false;
          }
        });
      }

      bindProof() {
        var root = this.querySelector('[data-rpv2-proof]');
        if (!root) return;
        var clips = parseJSON(root, '[data-rpv2-proof-clips]') || [];
        var trigger = root.querySelector('[data-rpv2-proof-trigger]');
        var modal = root.querySelector('[data-rpv2-proof-modal]');
        var video = root.querySelector('[data-rpv2-proof-video]');
        var loading = root.querySelector('[data-rpv2-proof-loading]');
        var tabs = Array.from(root.querySelectorAll('[data-rpv2-proof-idx]'));
        if (!clips.length || !trigger || !modal || !video) return;

        var self = this;
        var lastFocus = null;
        var current = 0;
        var probe = document.createElement('video');
        var canWebm = !!(
          probe.canPlayType &&
          (probe.canPlayType('video/webm; codecs="vp9"') || probe.canPlayType('video/webm'))
        );
        var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        var urlFor = function (clip) {
          return canWebm && clip.webm ? clip.webm : clip.mp4;
        };
        var playSafe = function (target) {
          var promise = target && target.play();
          if (promise && typeof promise.catch === 'function') promise.catch(function () {});
        };
        var showLoading = function () {
          if (loading && !modal.hidden) loading.classList.add('is-loading');
        };
        var hideLoading = function () {
          if (loading) loading.classList.remove('is-loading');
        };

        /* Only the selected design's poster loads with the page. Video bytes
           are requested after opening the dialog and released on close. */

        var loadClip = function (index, autoplay) {
          var clip = clips[index];
          if (!clip) return;
          current = index;
          video.poster = clip.poster || '';
          showLoading();
          video.src = urlFor(clip);
          video.muted = false;
          video.currentTime = 0;
          video.load();
          if (autoplay) playSafe(video);
          tabs.forEach(function (tab) {
            var active = parseInt(tab.dataset.rpv2ProofIdx, 10) === index;
            tab.classList.toggle('is-active', active);
            tab.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
        };

        var onKey = function (event) {
          if (event.key === 'Escape') closeModal();
        };
        var openModal = function () {
          lastFocus = document.activeElement;
          modal.hidden = false;
          document.body.classList.add('rpv2-dialog-open');
          loadClip(0, true);
          var close = modal.querySelector('[data-rpv2-proof-close]');
          if (close) close.focus();
          document.addEventListener('keydown', onKey);
          self.publish('robopixel:proof_opened', self.eventPayload());
        };
        var closeModal = function () {
          modal.hidden = true;
          document.body.classList.remove('rpv2-dialog-open');
          video.pause();
          video.removeAttribute('src');
          video.load();
          hideLoading();
          document.removeEventListener('keydown', onKey);
          if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
        };

        video.addEventListener('canplay', hideLoading);
        video.addEventListener('playing', hideLoading);
        video.addEventListener('error', hideLoading);
        video.addEventListener('waiting', showLoading);
        video.addEventListener('loadstart', showLoading);
        video.addEventListener('ended', function () {
          if (!modal.hidden && clips.length > 1) loadClip((current + 1) % clips.length, true);
        });
        trigger.addEventListener('click', openModal);
        tabs.forEach(function (tab) {
          tab.addEventListener('click', function () {
            var index = parseInt(tab.dataset.rpv2ProofIdx, 10);
            loadClip(index, true);
            self.publish('robopixel:proof_clip_selected', self.eventPayload({ clip_index: index }));
          });
        });
        root.querySelectorAll('[data-rpv2-proof-close]').forEach(function (button) {
          button.addEventListener('click', closeModal);
        });
      }
    }
  );
})();
